import { Card, Space, Button, Alert, Statistic, Typography } from 'antd'
import { AlertOutlined } from '@ant-design/icons'
import type { ReactNode } from 'react'

const { Text } = Typography

interface PriorityAction {
  key: string
  title: string
  description?: string
  urgency?: 'high' | 'medium' | 'low'
  metadata?: {
    label: string
    value: string | number
    suffix?: string
    valueColor?: string
  }
  action: {
    label: string
    onClick: () => void
    icon?: ReactNode
    type?: 'primary' | 'default' | 'dashed'
    danger?: boolean
  }
}

interface PriorityActionsPanelProps {
  actions: PriorityAction[]
  title?: string
  loading?: boolean
}

const urgencyConfig = {
  high: {
    type: 'error' as const,
    icon: <AlertOutlined />,
  },
  medium: {
    type: 'warning' as const,
    icon: <AlertOutlined />,
  },
  low: {
    type: 'info' as const,
    icon: <AlertOutlined />,
  },
}

/**
 * Priority Actions Panel
 *
 * Displays urgent tasks/actions that need attention at the top of a page.
 * Follows the design principle of "action-first" UX.
 *
 * Usage:
 * ```tsx
 * <PriorityActionsPanel
 *   actions={[
 *     {
 *       key: '1',
 *       title: 'Patient waiting for doctor assignment',
 *       urgency: 'high',
 *       metadata: {
 *         label: 'Waiting Time',
 *         value: 3,
 *         suffix: 'hours',
 *         valueColor: '#ef4444'
 *       },
 *       action: {
 *         label: 'Assign Doctor Now',
 *         onClick: () => {},
 *         type: 'primary'
 *       }
 *     }
 *   ]}
 * />
 * ```
 */
export function PriorityActionsPanel({ actions, title = '⚡ Needs Attention', loading }: PriorityActionsPanelProps) {
  if (actions.length === 0) {
    return null
  }

  return (
    <Card title={title} loading={loading} style={{ marginBottom: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {actions.map((action) => {
          const config = urgencyConfig[action.urgency || 'medium']

          return (
            <Alert
              key={action.key}
              message={action.title}
              description={
                action.description || action.metadata ? (
                  <Space direction="vertical" style={{ width: '100%', marginTop: 12 }}>
                    {action.description && <Text type="secondary">{action.description}</Text>}
                    {action.metadata && (
                      <Statistic
                        title={action.metadata.label}
                        value={action.metadata.value}
                        suffix={action.metadata.suffix}
                        valueStyle={{
                          color: action.metadata.valueColor,
                          fontSize: 20
                        }}
                      />
                    )}
                    <Button
                      type={action.action.type || 'primary'}
                      size="large"
                      icon={action.action.icon}
                      onClick={action.action.onClick}
                      danger={action.action.danger}
                      block
                    >
                      {action.action.label}
                    </Button>
                  </Space>
                ) : (
                  <Button
                    type={action.action.type || 'primary'}
                    size="large"
                    icon={action.action.icon}
                    onClick={action.action.onClick}
                    danger={action.action.danger}
                    block
                    style={{ marginTop: 12 }}
                  >
                    {action.action.label}
                  </Button>
                )
              }
              type={config.type}
              showIcon
              icon={config.icon}
            />
          )
        })}
      </Space>
    </Card>
  )
}
